/**
 * Schema-surface snapshot generated from `codex-cli 0.144.5` with:
 * `codex app-server generate-json-schema --out <dir>` (without `--experimental`).
 *
 * This is a version lock for the gateway adapter. Presence in generated schema
 * does not by itself mean that a method is production-stable; app-server also
 * exposes legacy and documented experimental/development surfaces.
 */
export const CODEX_APP_SERVER_SCHEMA_SNAPSHOT = {
  codexCliVersion: "0.144.5",
  generatedWithoutExperimental: true,
} as const;

export const CODEX_SCHEMA_CLIENT_REQUEST_METHODS = [
  "initialize",
  "thread/start",
  "thread/resume",
  "thread/fork",
  "thread/archive",
  "thread/delete",
  "thread/unsubscribe",
  "thread/name/set",
  "thread/goal/set",
  "thread/goal/get",
  "thread/goal/clear",
  "thread/metadata/update",
  "thread/unarchive",
  "thread/compact/start",
  "thread/shellCommand",
  "thread/approveGuardianDeniedAction",
  "thread/rollback",
  "thread/list",
  "thread/loaded/list",
  "thread/read",
  "thread/inject_items",
  "skills/list",
  "skills/extraRoots/set",
  "hooks/list",
  "marketplace/add",
  "marketplace/remove",
  "marketplace/upgrade",
  "plugin/list",
  "plugin/installed",
  "plugin/read",
  "plugin/skill/read",
  "plugin/share/save",
  "plugin/share/updateTargets",
  "plugin/share/list",
  "plugin/share/checkout",
  "plugin/share/delete",
  "app/list",
  "fs/readFile",
  "fs/writeFile",
  "fs/createDirectory",
  "fs/getMetadata",
  "fs/readDirectory",
  "fs/remove",
  "fs/copy",
  "fs/watch",
  "fs/unwatch",
  "skills/config/write",
  "plugin/install",
  "plugin/uninstall",
  "turn/start",
  "turn/steer",
  "turn/interrupt",
  "review/start",
  "model/list",
  "modelProvider/capabilities/read",
  "experimentalFeature/list",
  "permissionProfile/list",
  "experimentalFeature/enablement/set",
  "mcpServer/oauth/login",
  "config/mcpServer/reload",
  "mcpServerStatus/list",
  "mcpServer/resource/read",
  "mcpServer/tool/call",
  "windowsSandbox/setupStart",
  "windowsSandbox/readiness",
  "account/login/start",
  "account/login/cancel",
  "account/logout",
  "account/rateLimits/read",
  "account/rateLimitResetCredit/consume",
  "account/usage/read",
  "account/workspaceMessages/read",
  "account/sendAddCreditsNudgeEmail",
  "feedback/upload",
  "command/exec",
  "command/exec/write",
  "command/exec/terminate",
  "command/exec/resize",
  "config/read",
  "externalAgentConfig/detect",
  "externalAgentConfig/import",
  "externalAgentConfig/import/readHistories",
  "config/value/write",
  "config/batchWrite",
  "configRequirements/read",
  "account/read",
  "fuzzyFileSearch",
] as const;

export const CODEX_SCHEMA_CLIENT_NOTIFICATION_METHODS = ["initialized"] as const;

export const CODEX_SCHEMA_SERVER_NOTIFICATION_METHODS = [
  "error",
  "thread/started",
  "thread/status/changed",
  "thread/archived",
  "thread/deleted",
  "thread/unarchived",
  "thread/closed",
  "skills/changed",
  "thread/name/updated",
  "thread/goal/updated",
  "thread/goal/cleared",
  "thread/settings/updated",
  "thread/tokenUsage/updated",
  "turn/started",
  "hook/started",
  "turn/completed",
  "hook/completed",
  "turn/diff/updated",
  "turn/plan/updated",
  "item/started",
  "item/autoApprovalReview/started",
  "item/autoApprovalReview/completed",
  "item/completed",
  "item/agentMessage/delta",
  "command/exec/outputDelta",
  "process/outputDelta",
  "process/exited",
  "item/commandExecution/outputDelta",
  "item/commandExecution/terminalInteraction",
  "item/fileChange/outputDelta",
  "item/fileChange/patchUpdated",
  "serverRequest/resolved",
  "item/mcpToolCall/progress",
  "mcpServer/oauthLogin/completed",
  "mcpServer/startupStatus/updated",
  "account/updated",
  "account/rateLimits/updated",
  "app/list/updated",
  "remoteControl/status/changed",
  "externalAgentConfig/import/progress",
  "externalAgentConfig/import/completed",
  "fs/changed",
  "item/reasoning/summaryTextDelta",
  "item/reasoning/summaryPartAdded",
  "item/reasoning/textDelta",
  "item/plan/delta",
  "thread/compacted",
  "model/rerouted",
  "model/verification",
  "turn/moderationMetadata",
  "model/safetyBuffering/updated",
  "warning",
  "guardianWarning",
  "deprecationNotice",
  "configWarning",
  "fuzzyFileSearch/sessionUpdated",
  "fuzzyFileSearch/sessionCompleted",
  "thread/realtime/started",
  "thread/realtime/itemAdded",
  "thread/realtime/transcript/delta",
  "thread/realtime/transcript/done",
  "thread/realtime/outputAudio/delta",
  "thread/realtime/sdp",
  "thread/realtime/error",
  "thread/realtime/closed",
  "windows/worldWritableWarning",
  "windowsSandbox/setupCompleted",
  "account/login/completed",
] as const;

export const CODEX_SCHEMA_SERVER_REQUEST_METHODS = [
  "item/commandExecution/requestApproval",
  "item/fileChange/requestApproval",
  "mcpServer/elicitation/request",
  "item/permissions/requestApproval",
  "item/tool/requestUserInput",
  "item/tool/call",
  "account/chatgptAuthTokens/refresh",
  "attestation/generate",
  "applyPatchApproval",
  "execCommandApproval",
] as const;

export const CLIENT_INTERACTIVE_CODEX_SERVER_REQUEST_METHODS = [
  "item/commandExecution/requestApproval",
  "item/fileChange/requestApproval",
  "mcpServer/elicitation/request",
  "item/permissions/requestApproval",
] as const;

export const TRUSTED_HOST_CODEX_SERVER_REQUEST_METHODS = [
  "account/chatgptAuthTokens/refresh",
  "attestation/generate",
] as const;

export const DOCUMENTED_EXPERIMENTAL_CODEX_METHODS = [
  "item/tool/call",
  "item/tool/requestUserInput",
  "item/plan/delta",
] as const;

export const LEGACY_CODEX_SERVER_REQUEST_METHODS = [
  "applyPatchApproval",
  "execCommandApproval",
] as const;

export type CodexSchemaClientRequestMethod =
  (typeof CODEX_SCHEMA_CLIENT_REQUEST_METHODS)[number];
export type CodexSchemaClientNotificationMethod =
  (typeof CODEX_SCHEMA_CLIENT_NOTIFICATION_METHODS)[number];
export type CodexSchemaServerNotificationMethod =
  (typeof CODEX_SCHEMA_SERVER_NOTIFICATION_METHODS)[number];
export type CodexSchemaServerRequestMethod =
  (typeof CODEX_SCHEMA_SERVER_REQUEST_METHODS)[number];

const clientRequests = new Set<string>(CODEX_SCHEMA_CLIENT_REQUEST_METHODS);
const clientNotifications = new Set<string>(
  CODEX_SCHEMA_CLIENT_NOTIFICATION_METHODS,
);
const serverNotifications = new Set<string>(
  CODEX_SCHEMA_SERVER_NOTIFICATION_METHODS,
);
const serverRequests = new Set<string>(CODEX_SCHEMA_SERVER_REQUEST_METHODS);

export function isCodexSchemaClientRequestMethod(
  value: string,
): value is CodexSchemaClientRequestMethod {
  return clientRequests.has(value);
}

export function isCodexSchemaClientNotificationMethod(
  value: string,
): value is CodexSchemaClientNotificationMethod {
  return clientNotifications.has(value);
}

export function isCodexSchemaServerNotificationMethod(
  value: string,
): value is CodexSchemaServerNotificationMethod {
  return serverNotifications.has(value);
}

export function isCodexSchemaServerRequestMethod(
  value: string,
): value is CodexSchemaServerRequestMethod {
  return serverRequests.has(value);
}
